import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { cloneElement, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
// Custom hover tooltip — renders into document.body via portal so it can
// escape clipped/overflow:hidden ancestors, and positions itself off the
// trigger's bounding rect. Built for KTK-200 (channel agent roster) and
// reusable for any future hover-info surface that needs richer content
// than the native `title=` attribute permits.
export function HoverTooltip({ content, children, gap = 6, align = "right", showDelayMs = 80, }) {
    const triggerRef = useRef(null);
    const showTimerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const measure = () => {
        const el = triggerRef.current;
        if (!el)
            return;
        const r = el.getBoundingClientRect();
        setPos({
            top: r.bottom + gap,
            left: align === "left"
                ? r.left
                : align === "center"
                    ? r.left + r.width / 2
                    : r.right,
        });
    };
    useEffect(() => {
        if (!open)
            return;
        measure();
        const onScroll = () => measure();
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onScroll);
        };
    }, [open]);
    const cancelShow = () => {
        if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
    };
    const handleEnter = () => {
        cancelShow();
        showTimerRef.current = setTimeout(() => setOpen(true), showDelayMs);
    };
    const handleLeave = () => {
        cancelShow();
        setOpen(false);
    };
    // Inject hover handlers + ref into the trigger child without requiring the
    // caller to wire them manually. React 19 forwards `ref` through cloneElement
    // for function and host components alike.
    const trigger = cloneElement(children, {
        ref: (el) => {
            triggerRef.current = el;
            const childRef = children.props?.ref;
            if (typeof childRef === "function")
                childRef(el);
            else if (childRef && typeof childRef === "object")
                childRef.current = el;
        },
        onMouseEnter: handleEnter,
        onMouseLeave: handleLeave,
    });
    // Translate the anchor based on align so the tooltip sits where expected.
    const transform = align === "left"
        ? "translate(0, 0)"
        : align === "center"
            ? "translate(-50%, 0)"
            : "translate(-100%, 0)";
    return (_jsxs(_Fragment, { children: [trigger, open && pos
                ? createPortal(_jsx("div", { role: "tooltip", style: {
                        position: "fixed",
                        top: pos.top,
                        left: pos.left,
                        transform,
                        zIndex: 1000,
                        backgroundColor: "var(--bg-secondary)",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                        padding: "8px 0",
                        minWidth: 200,
                        maxWidth: 320,
                        fontSize: 12,
                        color: "var(--text-primary)",
                        pointerEvents: "none",
                    }, children: content }), document.body)
                : null] }));
}
//# sourceMappingURL=HoverTooltip.js.map